import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { style } from './PickerSmartphone.style';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { useStore } from 'src/contexts/StoreContext';
import { SecondaryButton } from 'src/editor-v3/components/buttons/secondary-button/SecondaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { brand, icon } from 'src/theme/colors';
import { useEffect, useState } from 'react';
import Clipboard from '@react-native-clipboard/clipboard';
import QRCode from 'react-native-qrcode-svg';
import Spinner from '@photosi/rn-components/spinner/Spinner';
import { DynamicModalProps } from '../../DynamicModal.types';
import { EDITOR_BASE_URL } from 'src/app/settings';
import { ROUTE_PATHS } from 'src/routes/_shared/routes.constants';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { SourcesEventType } from '../ModalUploadMultisource.constants';

export interface PickerSmartphoneProps extends DynamicModalProps {
  isOnline: boolean;
}

export const PickerSmartphone = observer((props: PickerSmartphoneProps) => {
  const {
    i18n,
    project: { createShareTokenMobile, shareTokenMobile },
  } = useStore();
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  useEffect(() => {
    if (props.isOnline) {
      if (!shareTokenMobile) {
        createShareTokenMobile().then(res => {
          generateUrl(res);
        });
      } else {
        generateUrl(shareTokenMobile);
      }
    }
  }, [props.isOnline]);

  const generateUrl = (token: string) => {
    const urlUploadMobile = `${EDITOR_BASE_URL}/${ROUTE_PATHS.UPLOAD_SMARTPHONE}?shareToken=${token}`;
    setUrl(urlUploadMobile);
  };
  const copyUrl = async () => {
    Clipboard.setString(`${url}&origin=copy_link`);
    setShowTooltip(true);
    setTimeout(() => {
      setShowTooltip(false);
    }, 2000);

    amplitude.trackEvent(AnalyticsEvents.dynamicPicker.shareLink, {
      type: 'copy_link',
      share_token_id: shareTokenMobile,
      source_type: SourcesEventType.Smartphone,
    });
  };

  const InstructionsContent = () => {
    return (
      <View style={style.instructions}>
        <TextLato style={style.stepsTitle}>{i18n.t('modal-picker-smartphone:upload')}</TextLato>
        <View style={style.stepsWrapper}>
          {[1, 2, 3].map(step => {
            return (
              <View style={style.step} key={`picker-smartphone-step-${step}`}>
                <TextLato style={style.stepNumber}>{step}</TextLato>
                <View>
                  <TextLato style={style.stepTitle}>{i18n.t(`modal-picker-smartphone:step-${step}-title`)}</TextLato>
                  <TextLato style={style.stepDescription}>
                    {i18n.t(`modal-picker-smartphone:step-${step}-description`)}
                  </TextLato>
                </View>
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  const ConnectedContent = () => {
    return (
      <View style={style.connected}>
        <View style={style.connectedIcon}>
          <Icon name={ICON_NAMES.Smartphone2} color={brand.main.water} />
        </View>
        <View style={style.textWrapper}>
          <TextLato style={style.connectedTitle}>{i18n.t('modal-picker-smartphone:connected-title')}</TextLato>
          <TextLato style={style.connectedDescription} numberOfLines={2}>
            {i18n.t('modal-picker-smartphone:connected-description')}
          </TextLato>
        </View>
      </View>
    );
  };

  return (
    <View style={style.wrapper}>
      <View style={{ gap: 24 }}>
        <View style={style.topContent}>
          <View style={style.qrCodeWrapper}>
            {url.length ? <QRCode size={145} value={`${url}&origin=qr_code`} /> : <Spinner />}
          </View>
          {isConnected ? <ConnectedContent /> : <InstructionsContent />}
        </View>
        <View style={style.separator} />
        <View style={style.bottomContent}>
          <TextLato style={style.bottomText}>{i18n.t('modal-picker-smartphone:troubleshooting')}</TextLato>
          <View>
            {showTooltip && (
              <View style={style.tooltip}>
                <View style={style.tooltipIcon}>
                  <Icon name={ICON_NAMES.Check} filled color={icon.iconSuccess} />
                </View>
                <TextLato style={style.tooltipText}>{i18n.t('modal-picker-smartphone:copied')}</TextLato>
              </View>
            )}
            <SecondaryButton
              onPress={copyUrl}
              focusId={`picker-smartphone-step-button-copy-url`}
              focusOrder={0}
              focusGroup={FocusGroups.LOOKUP_CHOICE}
              style={{ minHeight: 38, paddingHorizontal: 24 }}
              disabled={!url.length}
              text={i18n.t('modal-picker-smartphone:copy-cta')}
              textStyle={style.ctaText}
              iconName={ICON_NAMES.Copy}
              iconStyle={[style.ctaIcon, { color: brand.main.water }]}
              size={BUTTON_SIZES.REGULAR}
              type={BUTTON_TYPES.ICON_LEFT}
              layout={BUTTON_LAYOUTS.HUG}
            />
          </View>
        </View>
      </View>
    </View>
  );
});
